/**
 * Shura Nexus™ — The Living Relationship Intelligence Network.
 * ─────────────────────────────────────────────────────────────
 * Top-level page; pulls everything together:
 *
 *   • <NexusCanvas>    — 2D force-directed graph (HTML canvas, 60fps)
 *   • <ShuraNexus3D>   — 3D WebGL view (react-three-fiber)
 *   • <NodePanel>      — drill-in side panel with Shura Score breakdown
 *   • <RightRail>      — AI Discoveries · Legend · Time Travel
 *   • <AgendaModal>    — Iqra-generated meeting agenda (Phase 4.1)
 *
 * This file is intentionally lean — all DOM-heavy logic lives in
 * /components/shura/*. Keep it that way going forward.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Play, Pause, Maximize2, Flame, Box, Square } from 'lucide-react';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';

import ShuraNexus3D from './ShuraNexus3D';
import { NexusCanvas } from '@/components/shura/NexusCanvas';
import { NodePanel, StatPill } from '@/components/shura/NodePanel';
import { RightRail } from '@/components/shura/RightRail';
import { AgendaModal } from '@/components/shura/AgendaModal';
import NexusTicker from '@/components/shura/NexusTicker';
import NodeSearchBar from '@/components/shura/NodeSearchBar';
import FilterChips, { FILTER_KIND_MAP } from '@/components/shura/FilterChips';
import LiveConnectPanel from '@/components/shura/LiveConnectPanel';

export default function ShuraNexus() {
  const { effective } = useTheme();
  const isDark = effective === 'dark';

  const [graph, setGraph] = useState({ nodes: [], edges: [], stats: { nodes_total: 0, edges_total: 0, by_kind: {} } });
  const [selectedId, setSelectedId] = useState(null);
  const [discoveries, setDiscoveries] = useState([]);
  const [narratives, setNarratives] = useState({});
  const [loadingNarrative, setLoadingNarrative] = useState(null);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [multiSelectIds, setMultiSelectIds] = useState([]);
  const [timeYear, setTimeYear] = useState(2026);
  const [paused, setPaused] = useState(false);
  const [viewMode, setViewMode] = useState('2d');
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [heatmap, setHeatmap] = useState(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [agendaMeeting, setAgendaMeeting] = useState(null);
  const [creatingShura, setCreatingShura] = useState(false);
  // v168 — Node search + kind filters + Live Connect state
  const [searchHighlight, setSearchHighlight] = useState(null);
  const [activeFilters, setActiveFilters] = useState(() => new Set());
  // v168 — Live-paint state. When a connection succeeds we stash the
  // new edge's key (src|dst|kind) here with a `bornAt` timestamp; the
  // NexusCanvas renders these edges with a violet marching-ants dash
  // for 10s, then drops the entry on the next refresh.
  const [newEdgeKeys, setNewEdgeKeys] = useState(() => new Set());
  const [newEdgeBornAt, setNewEdgeBornAt] = useState(0);

  // Load graph + discoveries + heatmap on mount
  useEffect(() => {
    api.get('/shura/graph')
      .then(({ data }) => setGraph(data))
      .catch(() => toast.error('Failed to load ecosystem graph'));
    api.post('/shura/discover')
      .then(({ data }) => setDiscoveries(data.suggestions || []))
      .catch(() => { /* silent */ });
    api.get('/shura/heatmap')
      .then(({ data }) => setHeatmap(data))
      .catch(() => { /* silent */ });
  }, []);

  const togglePause = () => setPaused((p) => !p);

  const handleMultiSelect = useCallback((id) => {
    setMultiSelectIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  }, []);

  const createShura = async () => {
    if (multiSelectIds.length < 2) {
      toast.error('Select at least 2 entities first.');
      return;
    }
    setCreatingShura(true);
    try {
      const { data } = await api.post('/shura/multi-party-meeting', {
        node_ids: multiSelectIds,
        title: `Shura · ${new Date().toLocaleDateString()}`,
      });
      toast.success(`Shura created — Iqra agenda ready for ${data.participants.length} participants.`);
      setMultiSelectIds([]);
      setMultiSelectMode(false);
      setAgendaMeeting(data);  // pops the AgendaModal
      // Refresh discoveries — a new shura may shift the collaboration ranking.
      api.post('/shura/discover').then(({ data: d }) => setDiscoveries(d.suggestions || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create Shura');
    } finally {
      setCreatingShura(false);
    }
  };

  const regenerateAgenda = async () => {
    if (!agendaMeeting) return;
    try {
      const { data } = await api.post(`/shura/multi-party-meeting/${agendaMeeting.meeting_id}/regenerate-agenda`);
      setAgendaMeeting((prev) => prev && { ...prev, agenda_md: data.agenda_md });
      toast.success('Iqra refreshed the agenda.');
    } catch {
      toast.error('Iqra could not refresh the agenda.');
    }
  };

  const fetchNarrative = async (d) => {
    const key = [d.a.id, d.b.id].sort().join('|');
    if (narratives[key]) return;
    setLoadingNarrative(key);
    try {
      const { data } = await api.post('/shura/narrative', { a_id: d.a.id, b_id: d.b.id });
      setNarratives((prev) => ({ ...prev, [key]: data.narrative }));
    } catch {
      toast.error('Iqra narrative unavailable.');
    } finally {
      setLoadingNarrative(null);
    }
  };

  const stats = graph.stats || {};
  const topSector = useMemo(() => {
    const counts = {};
    graph.nodes.forEach((n) => {
      const sec = n.meta?.sector;
      if (sec) counts[sec] = (counts[sec] || 0) + 1;
    });
    const [top] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return top ? top[0] : '—';
  }, [graph.nodes]);

  // v168 — derive a kind→count map for the filter chip badges.
  const filterCounts = useMemo(() => {
    const out = {};
    Object.entries(FILTER_KIND_MAP).forEach(([filterId, kinds]) => {
      if (kinds[0]?.startsWith('__edge:')) {
        const edgeKind = kinds[0].slice('__edge:'.length);
        out[filterId] = (graph.edges || []).filter((e) => e.kind === edgeKind).length;
      } else {
        out[filterId] = (graph.nodes || []).filter((n) => kinds.includes(n.kind)).length;
      }
    });
    return out;
  }, [graph]);

  // v168 — apply node-kind filters to the graph that gets rendered.
  // When activeFilters is empty, show everything (default state).
  const filteredGraph = useMemo(() => {
    if (activeFilters.size === 0) return graph;
    const kinds = new Set();
    activeFilters.forEach((f) => {
      (FILTER_KIND_MAP[f] || []).forEach((k) => {
        if (!k.startsWith('__edge:')) kinds.add(k);
      });
    });
    const keep = (graph.nodes || []).filter((n) => kinds.size === 0 || kinds.has(n.kind));
    const keepIds = new Set(keep.map((n) => n.id));
    const edges = (graph.edges || []).filter((e) => keepIds.has(e.src) && keepIds.has(e.dst));
    return { ...graph, nodes: keep, edges };
  }, [graph, activeFilters]);

  const toggleFilter = useCallback((id) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const goldStrong = isDark ? '#8B3FC5' : '#5E1F8B';

  return (
    <div
      className={`-m-4 sm:-m-6 md:-m-8 min-h-[calc(100vh-80px)] ${isDark ? 'bg-[#050810] text-white' : 'bg-[#F7F5F4] text-[#0A0A0A]'}`}
      data-testid="shura-nexus"
    >
      {/* Header strip */}
      <div className={`px-4 sm:px-6 md:px-10 py-4 sm:py-5 border-b ${
        isDark ? 'border-white/10' : 'border-slate-200'
      }`}>
        <div className="flex items-start sm:items-center justify-between flex-wrap gap-3 sm:gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: goldStrong }}>
              Shura Nexus<sup className="text-[7px]">™</sup>
            </div>
            <h1 className={`display text-xl sm:text-2xl md:text-3xl mt-0.5 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
              The Living Ecosystem Network
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <StatPill value={stats.nodes_total || 0} label="Entities" isDark={isDark} />
            <StatPill value={stats.edges_total || 0} label="Connections" isDark={isDark} />
            <StatPill value={(stats.by_kind?.startup || 0)} label="Startups" isDark={isDark} />
            <StatPill value={topSector} label="Top sector" isDark={isDark} />

            {/* View mode toggle */}
            <div className={`flex items-center gap-1 p-1 rounded-lg border ${
              isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-slate-200'
            }`}>
              <ViewToggleBtn active={viewMode === '2d'} onClick={() => setViewMode('2d')} isDark={isDark} testId="shura-view-2d">
                <Square size={11} /> 2D
              </ViewToggleBtn>
              <ViewToggleBtn active={viewMode === '3d'} onClick={() => setViewMode('3d')} isDark={isDark} testId="shura-view-3d">
                <Box size={11} /> 3D
              </ViewToggleBtn>
            </div>

            {/* Heatmap toggle */}
            <button
              onClick={() => setHeatmapOn((v) => !v)}
              data-testid="shura-heatmap-toggle"
              disabled={viewMode === '3d'}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] rounded-lg border transition-colors flex items-center gap-1.5 ${
                heatmapOn
                  ? 'bg-rose-500/20 text-rose-500 border-rose-500/40'
                  : (isDark ? 'border-white/10 text-slate-300 hover:bg-white/5'
                            : 'border-slate-200 text-slate-600 hover:bg-white bg-white/70')
              } ${viewMode === '3d' ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={viewMode === '3d' ? 'Heatmap is 2D-only' : 'Toggle sector heatmap pulse'}
            >
              <Flame className="w-3 h-3" /> Heatmap {heatmapOn ? 'on' : 'off'}
            </button>

            {/* Create Shura button */}
            <button
              onClick={() => { setMultiSelectMode((m) => !m); setMultiSelectIds([]); }}
              data-testid="shura-multi-select-toggle"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] rounded-lg border transition-colors ${
                multiSelectMode
                  ? (isDark ? 'bg-[#8B3FC5] text-[#0A0A0A] border-[#8B3FC5]' : 'bg-[#5E1F8B] text-white border-[#5E1F8B]')
                  : (isDark ? 'border-[#8B3FC5]/40 text-[#8B3FC5] hover:bg-[#8B3FC5]/10'
                            : 'border-[#5E1F8B]/40 text-[#5E1F8B] hover:bg-[#5E1F8B]/10')
              }`}
            >
              <Users className="inline w-3 h-3 -mt-0.5 mr-1" />
              {multiSelectMode ? `Selecting (${multiSelectIds.length})` : 'Create Shura'}
            </button>
            {multiSelectMode && multiSelectIds.length >= 2 && (
              <button
                onClick={createShura}
                disabled={creatingShura}
                data-testid="shura-create-confirm"
                className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] rounded-lg bg-[#7C3AED] text-white hover:bg-[#8B5CF6] disabled:opacity-50"
              >
                {creatingShura ? 'Iqra thinking…' : `Create with ${multiSelectIds.length}`}
              </button>
            )}
          </div>
        </div>

        {/* v168 — Search + filter chips + Live Connect strip.
            Fills the gap between the title and the stat row that
            users flagged as empty. The search jumps the graph to the
            picked node; chips filter visible kinds; Live Connect
            triggers email + chat + a new graph edge. */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5">
            <NodeSearchBar
              nodes={graph.nodes || []}
              value={searchHighlight}
              onSelect={(n) => {
                setSearchHighlight(n?.id || null);
                if (n) setSelectedId(n.id);
              }}
              placeholder="Find a node…"
              isDark={isDark}
              testId="shura-node-search"
            />
          </div>
          <div className="lg:col-span-7 flex items-center justify-end">
            <FilterChips
              active={activeFilters}
              onToggle={toggleFilter}
              counts={filterCounts}
              isDark={isDark}
            />
          </div>
          <div className="lg:col-span-12">
            <LiveConnectPanel
              nodes={graph.nodes || []}
              isDark={isDark}
              onConnected={(result) => {
                // v168 — Mark the new edge for marching-ants paint.
                // Backend writes an `intro`-kind edge between the two
                // nodes; we precompute the key here so the canvas can
                // light it up the moment the refreshed graph lands.
                const key = `${result.from?.id}|${result.to?.id}|intro`;
                setNewEdgeKeys((prev) => {
                  const next = new Set(prev);
                  next.add(key);
                  return next;
                });
                setNewEdgeBornAt(Date.now());
                // Refresh the graph so the new edge is in the data.
                api.get('/shura/graph').then(({ data }) => setGraph(data)).catch(() => {});
                // Drop the highlight after 10s so subsequent renders
                // go back to the normal styling.
                setTimeout(() => {
                  setNewEdgeKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                  });
                }, 10_500);
              }}
            />
          </div>
        </div>
      </div>

      {/* Canvas + Right rail */}
      <div className="grid grid-cols-12 gap-4 px-4 sm:px-6 md:px-10 py-6">
        <div className="col-span-12">
          <NexusTicker />
        </div>
        <div className="col-span-12 lg:col-span-9 relative h-[400px] sm:h-[520px] lg:h-[680px]">
          {viewMode === '2d' ? (
            <NexusCanvas
              graph={filteredGraph}
              selectedId={selectedId}
              multiSelectMode={multiSelectMode}
              multiSelectIds={multiSelectIds}
              onSelect={setSelectedId}
              onMultiSelect={handleMultiSelect}
              heatmapOn={heatmapOn}
              heatmap={heatmap}
              timeYear={timeYear}
              isDark={isDark}
              resetSignal={resetSignal}
              newEdgeKeys={newEdgeKeys}
              newEdgeBornAt={newEdgeBornAt}
            />
          ) : (
            <ShuraNexus3D graph={filteredGraph} onSelect={setSelectedId} isDark={isDark} />
          )}
          <NodePanel nodeId={selectedId} onClose={() => setSelectedId(null)} isDark={isDark} />

          {/* Canvas controls (2D only) */}
          {viewMode === '2d' && (
            <div className={`absolute bottom-4 left-4 flex items-center gap-2 backdrop-blur-md rounded-xl p-2 border ${
              isDark ? 'bg-[#0A0F1A]/80 border-white/10' : 'bg-white/90 border-slate-200 shadow-sm'
            }`}>
              <button onClick={togglePause} data-testid="shura-pause"
                      className={`p-1.5 rounded ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                      title={paused ? 'Resume' : 'Pause'}>
                {paused
                  ? <Play size={14} style={{ color: goldStrong }} />
                  : <Pause size={14} style={{ color: goldStrong }} />}
              </button>
              <button data-testid="shura-fullscreen"
                      className={`p-1.5 rounded ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                      title="Reset view"
                      onClick={() => setResetSignal((s) => s + 1)}>
                <Maximize2 size={14} style={{ color: goldStrong }} />
              </button>
            </div>
          )}
        </div>

        <RightRail
          isDark={isDark}
          discoveries={discoveries}
          narratives={narratives}
          loadingNarrative={loadingNarrative}
          onAskIqra={fetchNarrative}
          stats={stats}
          timeYear={timeYear}
          onTimeYear={setTimeYear}
        />
      </div>

      <div className={`px-6 md:px-10 pb-10 text-[10px] text-center ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
        Phase 5 unlocks ecosystem-wide deal recommendations, predictive entity-risk scoring, and cross-tenant deal flow.
      </div>

      <AgendaModal
        open={!!agendaMeeting}
        meeting={agendaMeeting}
        isDark={isDark}
        onClose={() => setAgendaMeeting(null)}
        onRegenerate={regenerateAgenda}
      />
    </div>
  );
}

function ViewToggleBtn({ active, onClick, isDark, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded transition-colors flex items-center gap-1 ${
        active
          ? (isDark ? 'bg-[#8B3FC5] text-[#0A0A0A]' : 'bg-[#5E1F8B] text-white')
          : (isDark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-100')
      }`}
    >
      {children}
    </button>
  );
}
